/**
 * Iter39 — FLAC (Field-Level Access Control) frontend hook.
 *
 * Consumes the `_meta.flac` block returned by the backend on detail endpoints
 * (sale, client, contract) and exposes helpers to UI components.
 *
 * Iter40b — also accepts a standalone FLAC scope object (from
 * GET /api/me/field-access) for CREATE forms that have no GET payload yet.
 *
 * Usage:
 *   // EDIT: pass the response payload (uses _meta.flac block inside)
 *   const flac = useFieldAccess(sale);
 *
 *   // CREATE: pass the scope fetched separately
 *   const flac = useFieldAccess(scopeData, { mode: 'scope' });
 */
import { useMemo } from "react";

const ADMIN_ROLE = "admin";

export function useFieldAccess(payload, opts = {}) {
  return useMemo(() => {
    let meta = null;
    if (opts && opts.mode === "scope") {
      meta = payload || null;
    } else {
      meta = (payload && payload._meta && payload._meta.flac) || null;
    }
    const enforced = !!(meta && meta.enforced);
    const role = (meta && meta.role) || null;
    const hidden = new Set((meta && meta.hidden_fields) || []);
    const readOnly = new Set((meta && meta.read_only_fields) || []);

    const isHidden = (fieldKey) => enforced && hidden.has(fieldKey);
    const isReadOnly = (fieldKey) => enforced && readOnly.has(fieldKey);

    /**
     * Iter40 — canWrite(fieldKey): false if field is hidden or read-only for
     * the current role. Form submit handlers should EXCLUDE !canWrite fields
     * from the payload to avoid 403 from backend write enforcement.
     */
    const canWrite = (fieldKey) => {
      if (!enforced) return true; // admin
      if (hidden.has(fieldKey)) return false;
      if (readOnly.has(fieldKey)) return false;
      return true;
    };

    /**
     * Returns spread-able props for inputs/buttons.
     * If field is read-only:
     *   { disabled: true, title: "Read-only pentru rolul tău", "data-flac-readonly": true }
     * Otherwise returns empty object.
     */
    const readOnlyProps = (fieldKey) => {
      if (!isReadOnly(fieldKey)) return {};
      return {
        disabled: true,
        title: `Read-only pentru rolul „${role || "—"}". Contactează administratorul pentru editare.`,
        "data-flac-readonly": true,
      };
    };

    /**
     * Iter40 — strip non-writable keys from a payload before submitting.
     * Pass the entity_type prefix ("sale" | "client" | "contract") to map
     * leaf names to canonical field_keys (matching the catalog).
     */
    const stripNonWritable = (payload, prefix = "sale") => {
      if (!enforced || !payload || typeof payload !== "object") return payload;
      const out = {};
      for (const [leaf, value] of Object.entries(payload)) {
        const fk = `${prefix}.${leaf}`;
        if (canWrite(fk)) out[leaf] = value;
      }
      return out;
    };

    /**
     * Iter40b — strip non-writable keys for multi-entity payloads (e.g., QuickSale
     * which combines client + contract + sale fields in a flat dict).
     *
     * Provide a `fieldEntityMap`: { leaf_name → entity_prefix }
     * Defaults: standard CRM mapping for QuickSale-style payloads.
     */
    const stripNonWritableMulti = (payload, fieldEntityMap = null) => {
      if (!enforced || !payload || typeof payload !== "object") return payload;
      const defaultMap = {
        // client.* (legacy + new)
        client_type: "client", cnp: "client", cui: "client",
        first_name: "client", last_name: "client", full_name: "client",
        company_name: "client", phone_mobile: "client", phone_fixed: "client",
        email: "client", county: "client", locality: "client", street: "client",
        postal_code: "client", address_raw: "client",
        representative_name: "client", representative_cnp: "client",
        representative_phone: "client", representative_email: "client",
        legal_representative_name: "client", legal_representative_cnp: "client",
        consent_marketing: "client", consent_personal_data: "client",
        // contract.*
        pod: "contract", nlc: "contract", nlc_gaz: "contract", clc: "contract",
        contract_number: "contract", contract_number_old: "contract",
        commodity: "contract", product_name: "contract",
        commercial_product_name: "contract", contract_type: "contract",
        offer_type: "contract", customer_category: "contract",
        previous_supplier: "contract", supplier: "contract",
        distributor_name: "contract", distribution_zone: "contract",
        estimated_annual_kwh: "contract", signed_date: "contract", start_date: "contract",
        // sale.*
        channel: "sale", agent_name: "sale", agent_code: "sale",
        bo_operator_name: "sale", bo_received_at: "sale", bo_verified: "sale",
        uploaded_flag: "sale", uploaded_at: "sale",
        notes_agent: "sale", notes_bo: "sale",
        problems: "sale", problems_description: "sale", crm_internal_id: "sale",
        commission_month: "sale",
        // contract_status.*
        internal_status: "contract_status",
      };
      const map = fieldEntityMap || defaultMap;
      const out = {};
      for (const [leaf, value] of Object.entries(payload)) {
        if (leaf.startsWith("_") || leaf === "id" || leaf.endsWith("_id")) {
          out[leaf] = value;
          continue;
        }
        const prefix = map[leaf];
        if (!prefix) {
          // unmapped → passthrough (let backend decide)
          out[leaf] = value;
          continue;
        }
        const fk = `${prefix}.${leaf}`;
        if (canWrite(fk)) out[leaf] = value;
      }
      return out;
    };

    return {
      enforced,
      role,
      isAdmin: role === ADMIN_ROLE,
      hidden,
      readOnly,
      hiddenCount: hidden.size,
      readOnlyCount: readOnly.size,
      isHidden,
      isReadOnly,
      canWrite,
      readOnlyProps,
      stripNonWritable,
      stripNonWritableMulti,
    };
  }, [payload, opts && opts.mode]);
}

/**
 * Iter40b — fetch the current user's FLAC scope (/api/me/field-access) once
 * on mount, return a useFieldAccess-compatible object.
 *
 * Iter41 — refactored to read from AuthContext (one fetch per session,
 * shared across all components). Falls back to its own fetch only if the
 * AuthContext is not initialised yet.
 *
 * Used by CREATE forms (Client/Sale/Contract) that have no GET payload yet
 * but still want to disable read-only inputs proactively and strip non-writable
 * fields on submit.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export function useMyFieldAccess() {
  // Always read from AuthContext (single source of truth, Iter41)
  const { fieldAccess } = useAuth();
  // Fallback: standalone fetch only if AuthContext hasn't populated yet AND
  // the user is authenticated (axios has Authorization header).
  const [localScope, setLocalScope] = useState(null);

  useEffect(() => {
    if (fieldAccess) {
      // Context has the data — no fallback fetch needed
      return;
    }
    // Only run fallback once on mount if context is empty
    let alive = true;
    const t = setTimeout(async () => {
      if (!alive) return;
      // Re-check after a small delay (context might just be loading)
      try {
        const r = await axios.get(`${API_URL}/me/field-access`);
        if (alive) setLocalScope(r.data);
      } catch {
        if (alive) setLocalScope({ role: null, enforced: false, hidden_fields: [], read_only_fields: [] });
      }
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [fieldAccess]);

  return fieldAccess || localScope;
}

/**
 * Optional small banner component to surface FLAC scope to the user.
 * Renders nothing if FLAC is not enforced (admin viewers).
 */
export function FlacScopeBanner({ flac, entity = "această pagină" }) {
  if (!flac || !flac.enforced) return null;
  if (flac.hiddenCount === 0 && flac.readOnlyCount === 0) return null;
  return (
    <div
      className="rounded-md border border-sky-200 bg-sky-50/70 px-3 py-2 text-xs text-sky-900"
      data-testid="flac-scope-banner"
    >
      <span className="font-semibold">Vedere restricționată</span> — pentru rolul tău
      ({flac.role}) {flac.hiddenCount > 0 && (
        <>sunt <strong>{flac.hiddenCount}</strong> câmpuri ascunse</>
      )}
      {flac.hiddenCount > 0 && flac.readOnlyCount > 0 && " și "}
      {flac.readOnlyCount > 0 && (
        <>există <strong>{flac.readOnlyCount}</strong> câmpuri read-only</>
      )}{" "}
      pe {entity}. Configurabil în Administrare → Câmpuri & Acces.
    </div>
  );
}
